import { FC, useMemo } from "react";
import { Link } from "react-router";
import { Plus } from "lucide-react";

import { Restricted } from "../../../components/Restricted";
import { TitleWithStyles } from "../general";
import { LARGE_ICON_SIZE_STYLES } from "../../styles";
import { capitalizeAssociatedPress } from "../../../helpers/string/capitalize";
import { PureVoidFunc } from "../../../helpers/functions/types";
import { useAppSelector } from "../../../store";

export type ListWrapperProps = {
  title: string;
  newItemTitle: string;
  createPermissions: string;
  onDisabled?: PureVoidFunc;
  requirementTooltip?: string;
};

const SMALL_STYLES =
  "max-sm:z-20 max-sm:h-14 max-sm:w-14 max-sm:fixed  max-sm:right-4 max-sm:bottom-4 max-sm:rounded-2xl max-sm:focus:ring-2 max-sm:focus:ring-offset-2  max-sm:drop-shadow-base ";

const LARGE_STYLES = "sm:rounded-md sm:py-2.5  sm:focus:ring-1 sm:shadow-none ";

const GENERAL_STYLES =
  "flex justify-center align-center p-4  text-sm font-medium focus:outline-none focus:ring-stone-500  text-stone-400 ";

const linkStyles = (disabled?: boolean) =>
  `${GENERAL_STYLES} ${disabled ? "bg-stone-200 dark:bg-stone-700 cursor-not-allowed" : "sm:bg-black sm:hover:bg-stone-900 dark:text-black sm:dark:bg-white sm:dark:hover:bg-stone-100 sm:transition-colors  text-white bg-stone-500 dark:bg-stone-300 hover:bg-stone-600 "} ${SMALL_STYLES} ${LARGE_STYLES}`;

export const ListWrapper: FC<React.PropsWithChildren<ListWrapperProps>> = ({
  title,
  newItemTitle,
  createPermissions,
  children,
  onDisabled,
  requirementTooltip = undefined,
}) => {
  const displayTitle = capitalizeAssociatedPress(`Create New ${newItemTitle}`);
  const sitesReady = useAppSelector(
    (state) => state.sites.status === "fulfilled",
  );
  const companyReady = useAppSelector(
    (state) => state.company.status === "fulfilled",
  );

  const allowNew = useMemo(
    () => sitesReady && companyReady,
    [sitesReady, companyReady],
  );

  return (
    <div className="flex flex-col">
      <div className="mb-6 gap-4 sm:mb-8 sm:flex sm:items-center">
        <TitleWithStyles title={title} className="sm:flex-auto" />
        {allowNew && (
          <Restricted permissions={createPermissions}>
            <Link
              to="new"
              id={`createNew${newItemTitle}`}
              aria-disabled={!!onDisabled}
              title={requirementTooltip}
              className={linkStyles(!!onDisabled)}
              onClick={(e) => {
                if (onDisabled) {
                  e.preventDefault();
                  onDisabled();
                }
              }}
            >
              <Plus
                className={`${LARGE_ICON_SIZE_STYLES} sm:h-5 sm:w-5 sm:mr-2`}
              />
              <span className="sr-only sm:not-sr-only">{displayTitle}</span>
            </Link>
          </Restricted>
        )}
      </div>
      {children}
    </div>
  );
};
